import type { DocPage, DocSection } from "./schema.js";

const section = (value: DocSection): DocSection => value;

export const DOCS_VERSION = "0.5.0";

export const docs: readonly DocPage[] = [
  {
    id: "introduction",
    title: "Introduction",
    description: "Why BrowserMCP uses the browser as an MCP runtime.",
    path: "/docs/introduction",
    order: 1,
    audience: ["app-developer", "bridge-operator", "contributor"],
    status: "implemented",
    source: "docs/specification.md",
    related: ["architecture", "getting-started", "security-model"],
    sections: [
      section({
        id: "problem",
        title: "The problem BrowserMCP solves",
        summary:
          "Static web apps have rich local capabilities but no reusable path into MCP clients.",
        content: [
          "An MCP integration is often implemented as a separate server even when the real logic, data, and user context already live in a web app. This duplicates code and makes every app require another client configuration.",
          "BrowserMCP keeps execution in the browser. A shared local Bridge exposes the capabilities of connected web apps to MCP clients without teaching the Bridge application-specific business logic.",
        ],
        kind: "concept",
        status: "implemented",
        tags: ["problem", "static app", "runtime", "business logic", "shared bridge"],
        related: ["architecture/responsibility-boundaries"],
        source: "docs/specification.md",
      }),
      section({
        id: "browser-runtime",
        title: "The browser is the MCP runtime",
        summary: "Tools execute beside JavaScript, Workers, WASM, IndexedDB, Canvas, and Web APIs.",
        content: [
          "BrowserMCP handlers run in the connected page, so they can use browser APIs that are unavailable to a conventional server process. The site includes safe demonstrations for IndexedDB and Web Worker execution.",
          "The Bridge authenticates and routes requests; it does not move browser-owned implementation into the local Bridge or native app process.",
        ],
        kind: "concept",
        status: "implemented",
        tags: ["JavaScript", "Web Worker", "WASM", "IndexedDB", "Canvas", "Web API"],
        related: ["architecture/request-flow", "creating-app/minimal-app"],
        source: "docs/specification.md",
      }),
      section({
        id: "one-client-entry",
        title: "One MCP client entry",
        summary: "Configure the MCP client once for the common loopback Bridge.",
        content: [
          "MCP clients connect to http://127.0.0.1:8789/mcp using Streamable HTTP. Connected apps and their namespaced capabilities appear through that single endpoint.",
          "A first browser connection still needs an explicit operator decision for its exact observed Origin. A loopback address alone is not a trust boundary.",
        ],
        kind: "concept",
        status: "implemented",
        tags: ["MCP client", "Streamable HTTP", "single endpoint", "8789", "pairing"],
        related: ["configuring-mcp-client/client-entry", "security-model/pairing"],
        source: "docs/specification.md",
      }),
    ],
  },
  {
    id: "architecture",
    title: "Architecture",
    description: "Components, trust boundaries, and request routing.",
    path: "/docs/architecture",
    order: 2,
    audience: ["app-developer", "bridge-operator", "contributor"],
    status: "implemented",
    source: "docs/specification.md",
    related: ["bridge-protocol", "security-model", "multiple-applications"],
    sections: [
      section({
        id: "components",
        title: "Core components and optional macOS shell",
        summary: "MCP Client → cross-platform Bridge → BrowserMCP Web App.",
        content: [
          "The MCP client speaks standard MCP Streamable HTTP. The cross-platform Node.js Bridge converts standard MCP operations into the separate BrowserMCP Bridge Protocol. The web library converts protocol calls into declarative browser handlers.",
          "The protocol package contains shared wire types; the web package owns browser connection state; each web app owns its tools, resources, prompts, data, and browser API usage. On macOS, a native menu-bar app can supervise the same Bridge process without reimplementing protocol or routing state.",
        ],
        kind: "architecture",
        status: "implemented",
        tags: ["component", "MCP", "Bridge", "web library", "protocol"],
        related: ["bridge-protocol/envelope", "api-reference/browsermcp"],
        source: "docs/specification.md",
      }),
      section({
        id: "responsibility-boundaries",
        title: "Responsibility boundaries",
        summary: "Standard MCP terminates at the Bridge; app behavior stays in the browser.",
        content: [
          "The Bridge owns loopback listening, stateless MCP request handling, bounded notification subscriptions, authentication, routing, limits, cancellation, timeout enforcement, and MCP result conversion. It is application-agnostic.",
          "The browser app owns capability declarations, input validation beyond transport limits, user-visible context, and execution. The MCP client owns user intent and its own approval policy.",
          "Never add app-specific document search, storage semantics, or workflow logic to the Bridge. This documentation search implementation is registered by /site and executes in the page.",
        ],
        kind: "architecture",
        status: "implemented",
        tags: ["responsibility", "boundary", "business logic", "routing", "conversion"],
        related: ["security-model/trust-boundaries", "bridge-protocol/separation"],
        source: "docs/specification.md",
      }),
      section({
        id: "request-flow",
        title: "Request flow",
        summary:
          "Requests retain distinct MCP and Bridge Protocol identities across an asynchronous round trip.",
        content: [
          "The Bridge receives an MCP request, resolves a namespaced capability to one connected runtime, creates a Bridge Protocol request ID, and forwards an invoke message. The web library dispatches to the registered handler and returns either a result or a structured execution error.",
          "Timeout and cancellation propagate across the boundary. Disconnecting a runtime removes its capabilities and rejects requests that can no longer complete.",
        ],
        kind: "architecture",
        status: "implemented",
        tags: ["request ID", "invoke", "result", "timeout", "cancellation", "disconnect"],
        related: ["results-errors/error-shape", "connection-lifecycle/state-machine"],
        source: "docs/specification.md",
      }),
    ],
  },
  {
    id: "getting-started",
    title: "Getting Started",
    description: "A shortest-path setup from checkout to the first browser capability.",
    path: "/docs/getting-started",
    order: 3,
    audience: ["app-developer", "contributor"],
    status: "implemented",
    source: "Repository implementation",
    related: ["installing-macos-bridge", "configuring-mcp-client", "creating-app"],
    sections: [
      section({
        id: "prerequisites",
        title: "Prerequisites",
        summary: "Node.js 24+, npm 11+, a current browser, and optional Xcode for the macOS app.",
        content: [
          "The Node.js Bridge targets current macOS, Linux, and Windows with Node.js 24 or newer, npm 11 or newer, loopback IPv4 networking, a current stable browser, and an MCP 2026-07-28 Streamable HTTP client. HTTPS static apps additionally need an IP-valid local certificate trusted by the active browser/client.",
          "The native menu-bar app requires macOS 14 or newer, current Xcode to build, and external Node.js 24 or newer at runtime. Only Apple Silicon macOS 26.5.2 with Node.js 24.10.0 and Xcode 26.6 was verified; Linux, Windows, and Intel Mac remain unverified.",
        ],
        kind: "guide",
        status: "implemented",
        tags: [
          "requirements",
          "Node 24",
          "macOS",
          "Linux",
          "Windows",
          "Apple Silicon",
          "Xcode",
          "browser",
        ],
        related: ["development/workspace"],
        source: "docs/specification.md; package.json engines",
      }),
      section({
        id: "workspace-setup",
        title: "Install and validate the workspace",
        summary: "Install once at the repository root, then run the shared quality commands.",
        content: [
          "Run npm install at the repository root. Use npm run typecheck, npm test, npm run format:check, and npm run build to validate all workspaces.",
          "For the docs site, run npm run dev -w @browsermcp/site. Vite listens on loopback by default; no external service or deployment is required.",
        ],
        kind: "guide",
        status: "implemented",
        tags: ["install", "npm", "workspace", "Vite", "dev", "build"],
        examples: [
          {
            id: "workspace-commands",
            title: "Workspace setup",
            language: "shell",
            code: "npm install\nnpm run typecheck\nnpm test\nnpm run build\nnpm run dev -w @browsermcp/site",
            explanation: "Run from the repository root.",
          },
        ],
        related: ["development/quality-gates"],
        source: "Repository package scripts",
      }),
      section({
        id: "first-round-trip",
        title: "Complete the first round trip",
        summary: "Start the Bridge, approve the site, configure one MCP endpoint, then list tools.",
        content: [
          "Start the cross-platform Bridge from the CLI or, on macOS, through BrowserMCP.app. Open /site, request approval from the connection UI, then verify and approve the exact Origin and app identity in the authenticated Bridge management page. No browser-side token is required.",
          "Configure the MCP client for http://127.0.0.1:8789/mcp. Listing tools should include namespaced site and docs capabilities; calling docs_search should return source paths and section IDs.",
        ],
        kind: "guide",
        status: "implemented",
        tags: ["first run", "round trip", "Origin approval", "docs_search", "tool list"],
        related: ["installing-macos-bridge/start", "configuring-mcp-client/client-entry"],
        source: "docs/specification.md",
      }),
    ],
  },
  {
    id: "installing-macos-bridge",
    title: "Installing the Bridge and macOS App",
    description:
      "Run the cross-platform Bridge directly or supervise it with the native macOS app.",
    path: "/docs/installing-macos-bridge",
    order: 4,
    audience: ["bridge-operator", "contributor"],
    status: "implemented",
    source: "docs/specification.md",
    related: ["bridge-configuration", "security-model", "configuring-mcp-client"],
    sections: [
      section({
        id: "build",
        title: "Build and run the cross-platform Bridge",
        summary: "Use one Node.js 24 CLI on macOS, Linux, and Windows.",
        content: [
          "The authoritative Bridge is a Node.js 24 local process in /bridge with an authenticated status UI. Build it from the repository root with npm run build:bridge and start it with npm run start:bridge. The same CLI, configuration, loopback endpoints, PEM TLS inputs, and protocols target macOS, Linux, and Windows.",
          "On macOS Terminal, a Linux shell, or Windows PowerShell, run npm run start:bridge -- --port 8789. The process stays in the foreground; the Bridge package does not install launchd, systemd, or a Windows service. Request access from the browser and approve its exact Origin in the authenticated management page. For WSS, use the cross-platform Node/OpenSSL generator and manually configure the active OS/browser trust store.",
          "The Bridge core installs no OS service and changes no certificate trust. Only Apple Silicon macOS is verified; Linux, Windows, and Intel Mac require the reproducible platform checks in docs/verification.md before they become support claims.",
        ],
        kind: "guide",
        status: "implemented",
        tags: ["macOS", "Linux", "Windows", "Node 24", "build", "CLI", "cross-platform"],
        examples: [
          {
            id: "bridge-build-start",
            title: "Build and start the local Bridge",
            language: "shell",
            code: "npm run build:bridge\nnpm run start:bridge -- --port 8789",
            explanation:
              "The startup output prints ephemeral MCP and admin credentials once; keep that terminal private.",
          },
        ],
        related: ["roadmap/distribution"],
        source: "docs/specification.md",
      }),
      section({
        id: "start",
        title: "Build and use the native macOS app",
        summary: "An unsigned menu-bar app supervises the same Bridge process on macOS 14+.",
        content: [
          "Build macOS/BrowserMCPApp.xcodeproj with the BrowserMCPApp scheme and CODE_SIGNING_ALLOWED=NO, then launch BrowserMCP.app. Its MenuBarExtra status item opens a SwiftUI management window with Start, Stop, and Restart, Node/Bridge selection, endpoints, explicit startup-credential reveal/copy, pending exact-Origin approval with Approve/Reject actions, legacy token compatibility, app/browser-session/MCP-subscription/capability counts, status-page launch, logs, and lifecycle errors.",
          "The app prefers its dependency-bundled browsermcp-bridge.mjs resource and falls back to bridge/dist/cli.js for repository development. JavaScript dependencies are bundled, but Node.js 24 itself is external. The App Sandbox is disabled so the app can launch that runtime and use operator-selected files; executable selection is a local trust decision. Only non-secret launch preferences persist; credentials and process state stay in memory and are revoked when the Bridge stops.",
        ],
        kind: "guide",
        status: "implemented",
        tags: [
          "macOS app",
          "menu bar",
          "SwiftUI",
          "Node selection",
          "start",
          "stop",
          "restart",
          "unsigned",
        ],
        related: ["bridge-configuration/network"],
        source: "docs/specification.md; macOS/BrowserMCPApp.xcodeproj",
      }),
      section({
        id: "pair-site",
        title: "Approve a browser Origin",
        summary: "Approve the waiting connection for the exact observed site Origin.",
        content: [
          "Start browsermcp-bridge, request access from the web page, then enter the startup admin token in the local management UI. Verify the exact scheme, host, port, app, runtime, instance, and expiry before choosing Approve or Reject. The waiting socket has no session or registrations before approval.",
          "The --pair-origin flag and pairing-token API remain a legacy compatibility path. Do not place legacy tokens in source code, URL queries, logs, screenshots, or persistent browser storage.",
        ],
        kind: "security",
        status: "implemented",
        tags: ["pairing", "Origin", "short-lived token", "one-time", "secret"],
        examples: [
          {
            id: "pair-local-site",
            title: "Start the Bridge for local Vite development",
            language: "shell",
            code: "npm run start:bridge -- --port 8789",
            explanation:
              "Request approval from the Vite page, then approve its exact Origin in the Bridge management page.",
          },
        ],
        related: ["security-model/pairing", "troubleshooting/authentication-error"],
        source: "docs/specification.md",
      }),
    ],
  },
  {
    id: "configuring-mcp-client",
    title: "Configuring an MCP Client",
    description: "Register one Streamable HTTP endpoint for every connected browser app.",
    path: "/docs/configuring-mcp-client",
    order: 5,
    audience: ["app-developer", "bridge-operator"],
    status: "implemented",
    source: "docs/specification.md",
    related: ["getting-started", "multiple-applications", "troubleshooting"],
    sections: [
      section({
        id: "client-entry",
        title: "Add the common Bridge",
        summary: "Use Streamable HTTP at http://127.0.0.1:8789/mcp.",
        content: [
          "Create one MCP server entry named browsermcp whose URL is http://127.0.0.1:8789/mcp, whose transport is Streamable HTTP, and whose protocol revision is pinned to 2026-07-28. Add Authorization: Bearer <startup MCP token>, using the fresh token printed once each time the Bridge starts. Do not add an entry per web app.",
          "Exact settings syntax differs by client; preserve the protocol revision, transport, loopback URL, and Authorization header. The endpoint is POST-only and stateless: it does not implement initialize, Mcp-Session-Id, GET, or DELETE. The MCP bearer token is distinct from browser pairing and the admin token. Do not commit it to shared configuration. The client receives standard MCP and never connects directly to the browser WebSocket.",
        ],
        kind: "configuration",
        status: "implemented",
        tags: ["MCP client", "configuration", "Streamable HTTP", "URL", "single entry"],
        examples: [
          {
            id: "generic-client-config",
            title: "Generic MCP client entry",
            language: "json",
            code: '{\n  "mcpServers": {\n    "browsermcp": {\n      "transport": "streamable-http",\n      "url": "http://127.0.0.1:8789/mcp",\n      "headers": {\n        "Authorization": "Bearer <MCP_TOKEN_PRINTED_AT_STARTUP>"\n      }\n    }\n  }\n}',
            explanation:
              "Adapt the outer object to the client. Replace the placeholder after each Bridge start and keep the resulting config private.",
          },
        ],
        related: ["bridge-configuration/network"],
        source: "docs/specification.md",
      }),
      section({
        id: "verify",
        title: "Verify discovery and stateless requests",
        summary: "Discover the 2026-07-28 endpoint, then list tools, resources, and prompts.",
        content: [
          "After the site is paired, connect with MCP revision 2026-07-28 or refresh capability discovery in the MCP client. Every request carries its own client envelope and the lists include namespaced entries for each connected app runtime.",
          "If the Bridge responds but no site capabilities appear, inspect the Bridge app list and the site's connection panel before changing client configuration.",
        ],
        kind: "guide",
        status: "implemented",
        tags: [
          "2026-07-28",
          "stateless",
          "list tools",
          "list resources",
          "list prompts",
          "discovery",
        ],
        related: ["troubleshooting/bridge-not-connected"],
        source: "docs/specification.md",
      }),
      section({
        id: "client-trust",
        title: "Client-side approval",
        summary: "Capability discovery does not replace the MCP client's approval policy.",
        content: [
          "Treat browser-provided capability descriptions and results as data from the paired Origin. A client should retain user approval and input-size policy for consequential tools.",
          "The included site tools are read-only or origin-scoped demonstrations. Pairing another site can expose different behavior under a different namespace.",
        ],
        kind: "security",
        status: "implemented",
        tags: ["approval", "trust", "policy", "namespace", "Origin"],
        related: ["security-model/trust-boundaries"],
        source: "docs/specification.md",
      }),
    ],
  },
  {
    id: "creating-app",
    title: "Creating a BrowserMCP App",
    description: "Add the framework-free web library and declare browser capabilities.",
    path: "/docs/creating-app",
    order: 6,
    audience: ["app-developer"],
    status: "implemented",
    source: "docs/specification.md",
    related: ["tools", "resources", "prompts", "connection-lifecycle"],
    sections: [
      section({
        id: "minimal-app",
        title: "Create and connect an app",
        summary:
          "Instantiate BrowserMCP with stable app identity, register capabilities, then connect.",
        content: [
          "Install @browsermcp/web in a bundler project and create one BrowserMCP instance per web application runtime. App identity is stable across pages; runtime identity distinguishes tabs and reconnects.",
          "Register tools, resources, and prompts before connect so the initial capability snapshot is complete. Registrations may also change later and are synchronized to the Bridge.",
        ],
        kind: "guide",
        status: "implemented",
        tags: ["BrowserMCP", "constructor", "connect", "register", "runtime"],
        examples: [
          {
            id: "minimal-app-code",
            title: "Minimal BrowserMCP app",
            language: "typescript",
            code: 'import { BrowserMCP } from "@browsermcp/web";\n\nconst securePage = location.protocol === "https:";\nconst app = new BrowserMCP({\n  name: "Example App",\n  version: "0.1.0",\n  bridgeUrl: securePage\n    ? "wss://127.0.0.1:8789/browser"\n    : "ws://127.0.0.1:8789/browser",\n  prepareLocalNetworkAccess: securePage,\n});\n\napp.tool({\n  name: "example",\n  description: "Return safe page identity without query or fragment data",\n  inputSchema: { type: "object", additionalProperties: false },\n  handler: async () => ({\n    content: [{ type: "text", text: location.origin + location.pathname }],\n    structuredContent: { origin: location.origin, pathname: location.pathname },\n  }),\n});\n\nawait app.connect();',
            explanation:
              "connect() requests operator approval when no resume credential is available.",
          },
        ],
        related: ["security-model/pairing", "api-reference/browsermcp"],
        source: "docs/specification.md",
      }),
      section({
        id: "static-hosting",
        title: "Use static hosting",
        summary: "BrowserMCP needs no app-specific server and works with ESM and common bundlers.",
        content: [
          "The web library has no UI framework dependency and can be bundled by Vite or imported as ESM. The application can be served as static files, including a GitHub Pages HTTPS repository subpath build. This repository's Pages workflow is hosting automation only; BrowserMCP itself is independent of the static host.",
          "Public/non-loopback BrowserMCP apps must be served over HTTPS; the library rejects a remote HTTP Origin even with a wss Bridge because page code and pairing could be modified in transit. Plain HTTP is limited to localhost or 127.0.0.1 development.",
        ],
        kind: "guide",
        status: "implemented",
        tags: ["static hosting", "ESM", "Vite", "bundler", "framework-free"],
        related: ["development/site-architecture"],
        source: "docs/specification.md",
      }),
      section({
        id: "registration-lifecycle",
        title: "Manage registrations",
        summary:
          "Keep registration disposers and release page-scoped capabilities when context changes.",
        content: [
          "Registration methods return a RegistrationHandle with id, ready, and unregister(). Await ready when connected registration acknowledgement matters, and call unregister() when the capability is no longer valid; disconnecting removes every capability owned by that runtime from the Bridge.",
          "Names should be stable and local to the app. The Bridge applies the app namespace so another app can use the same local name without silent replacement.",
        ],
        kind: "api",
        status: "implemented",
        tags: ["registration", "unregister", "dispose", "page context", "capability"],
        related: ["multiple-applications/namespacing", "connection-lifecycle/disconnect"],
        source: "docs/specification.md",
      }),
    ],
  },
  {
    id: "tools",
    title: "Tools",
    description: "Register executable, schema-described browser operations.",
    path: "/docs/tools",
    order: 7,
    audience: ["app-developer"],
    status: "implemented",
    source: "docs/specification.md",
    related: ["results-errors", "api-reference", "resources"],
    sections: [
      section({
        id: "register-tool",
        title: "Register a tool",
        summary: "Provide a local name, description, JSON Schema, and asynchronous handler.",
        content: [
          "app.tool declares an operation executed in the browser. Use a closed input schema, validate semantic constraints in the handler, and return standard MCP content items.",
          "Descriptions should identify side effects and browser-owned state. The Bridge applies transport limits but cannot infer application business rules.",
        ],
        kind: "api",
        status: "implemented",
        tags: ["tool", "app.tool", "inputSchema", "handler", "JSON Schema", "registration"],
        examples: [
          {
            id: "tool-registration",
            title: "Register a typed greeting tool",
            language: "typescript",
            code: 'const greeting = app.tool({\n  name: "greet",\n  description: "Create a local greeting with no side effects",\n  inputSchema: {\n    type: "object",\n    properties: { name: { type: "string", maxLength: 80 } },\n    required: ["name"],\n    additionalProperties: false,\n  },\n  handler: async ({ name }) => ({\n    content: [{ type: "text", text: "Hello, " + String(name) }],\n  }),\n});\n\n// Later: await greeting.unregister();',
            explanation: "The registration can be removed without disconnecting the app.",
          },
        ],
        related: ["results-errors/success-results"],
        source: "docs/specification.md",
      }),
      section({
        id: "tool-safety",
        title: "Design safe tools",
        summary:
          "Constrain input, make effects explicit, support cancellation, and avoid secret output.",
        content: [
          "Set practical string, array, and object limits in JSON Schema. Honor the supplied AbortSignal for long operations and prefer idempotent operations when retries are possible.",
          "Never expose pairing tokens, browser credentials, cross-origin data, or arbitrary script evaluation. Require user interaction when a browser API demands it and report that requirement as a structured error.",
        ],
        kind: "security",
        status: "implemented",
        tags: ["safe tool", "AbortSignal", "limits", "secret", "side effect", "idempotent"],
        related: ["security-model/input-limits", "results-errors/errors"],
        source: "docs/specification.md",
      }),
      section({
        id: "site-tools",
        title: "Tools exposed by this site",
        summary:
          "Docs, navigation context, runtime inspection, origin-scoped storage, Worker computation, and an interactive Rubik's Cube benchmark.",
        content: [
          "The site registers docs_search, docs_get_page, docs_get_section, docs_search_api, docs_search_types, docs_find_examples, docs_troubleshoot, docs_implementation_guide, docs_responsibility, docs_capabilities, docs_related, site_current_page, site_structure, site_navigation, site_runtime, site_status, site_storage_put, site_storage_get, site_worker_analyze, rubiks_cube_get_state, rubiks_cube_apply_moves, rubiks_cube_scramble, rubiks_cube_reset, and rubiks_cube_set_autoplay.",
          "Navigation is reported but MCP-triggered arbitrary navigation is intentionally omitted. Storage keys and values are size-limited and confined to this Origin's IndexedDB database; Worker analysis accepts text only. The Rubik's Cube Tools inspect or mutate one shared browser-owned benchmark state through bounded move, scramble, reset, and autoplay operations.",
        ],
        kind: "status",
        status: "implemented",
        tags: [
          "site tools",
          "docs_search",
          "IndexedDB",
          "Worker",
          "current page",
          "runtime",
          "Rubik's Cube",
          "benchmark",
        ],
        related: ["resources/site-resources", "prompts/site-prompts"],
        source: "site/src/browsermcp/registration.ts",
      }),
    ],
  },
  {
    id: "resources",
    title: "Resources",
    description:
      "Expose addressable browser and documentation content without execution semantics.",
    path: "/docs/resources",
    order: 8,
    audience: ["app-developer"],
    status: "implemented",
    source: "docs/specification.md",
    related: ["tools", "prompts", "api-reference"],
    sections: [
      section({
        id: "register-resource",
        title: "Register a resource",
        summary: "Map a stable URI and metadata to an asynchronous read handler.",
        content: [
          "app.resource registers content identified by an application URI. Return text or binary resource contents with an accurate MIME type and avoid using a resource read for state-changing operations.",
          "A resource may reflect live page state, but callers should treat each read as a fresh snapshot.",
        ],
        kind: "api",
        status: "implemented",
        tags: ["resource", "app.resource", "URI", "MIME", "read"],
        examples: [
          {
            id: "resource-registration",
            title: "Register a page resource",
            language: "typescript",
            code: 'app.resource({\n  uri: "browsermcp://example/current-page",\n  name: "Current page",\n  mimeType: "application/json",\n  handler: async () => ({\n    contents: [{\n      uri: "browsermcp://example/current-page",\n      mimeType: "application/json",\n      text: JSON.stringify({ path: location.pathname }),\n    }],\n  }),\n});',
            explanation: "The value is computed in the browser when the resource is read.",
          },
        ],
        related: ["api-reference/registration-types"],
        source: "docs/specification.md",
      }),
      section({
        id: "site-resources",
        title: "Documentation resources",
        summary:
          "The complete corpus, page URIs, status inventory, site state, and Rubik's Cube benchmark state are addressable resources.",
        content: [
          "browsermcp://docs/index lists page IDs, paths, descriptions, status, and related pages. browsermcp://docs/page/{pageId} reads one structured page. browsermcp://docs/status groups implemented, partial, planned, and constrained sections.",
          "browsermcp://site/current-page and browsermcp://site/status expose live route and connection snapshots. browsermcp://benchmark/rubiks-cube/state exposes the same live cube state shared by the landing hero, architecture connection segment, and connection page. All documentation values come from the same typed corpus rendered by the UI.",
        ],
        kind: "status",
        status: "implemented",
        tags: [
          "docs resource",
          "resource template",
          "index",
          "status",
          "current page",
          "Rubik's Cube",
          "benchmark",
        ],
        related: ["prompts/site-prompts"],
        source: "site/src/browsermcp/registration.ts",
      }),
      section({
        id: "resource-errors",
        title: "Resource errors",
        summary:
          "Missing or unavailable resources return explicit not-found or disconnected errors.",
        content: [
          "A missing page or section is not represented by empty success content. The handler returns a structured not-found error with related document suggestions.",
          "When the owning runtime disconnects, the Bridge removes its resource registrations; stale reads fail as capability unavailable rather than being routed elsewhere.",
        ],
        kind: "api",
        status: "implemented",
        tags: ["resource error", "not found", "disconnect", "stale", "suggestion"],
        related: ["results-errors/errors"],
        source: "docs/specification.md",
      }),
    ],
  },
  {
    id: "prompts",
    title: "Prompts",
    description: "Publish reusable, argument-driven development workflows.",
    path: "/docs/prompts",
    order: 9,
    audience: ["app-developer"],
    status: "implemented",
    source: "docs/specification.md",
    related: ["resources", "tools", "troubleshooting"],
    sections: [
      section({
        id: "register-prompt",
        title: "Register a prompt",
        summary: "Declare prompt arguments and produce MCP prompt messages in the browser.",
        content: [
          "app.prompt registers a named prompt with a description, argument declarations, and a handler. Validate argument length and interpolate values as data, never as executable code.",
          "Prompts guide an agent toward the relevant Tools and Resources; they do not bypass capability permissions.",
        ],
        kind: "api",
        status: "implemented",
        tags: ["prompt", "app.prompt", "arguments", "messages", "handler"],
        examples: [
          {
            id: "prompt-registration",
            title: "Register an implementation prompt",
            language: "typescript",
            code: 'app.prompt({\n  name: "implement_browsermcp_feature",\n  description: "Plan a BrowserMCP feature from the docs corpus",\n  arguments: [{ name: "goal", required: true }],\n  handler: async ({ goal }) => ({\n    messages: [{\n      role: "user",\n      content: { type: "text", text: "Use docs_search for: " + goal },\n    }],\n  }),\n});',
            explanation:
              "The prompt points the client at source-backed documentation capabilities.",
          },
        ],
        related: ["tools/site-tools"],
        source: "docs/specification.md",
      }),
      section({
        id: "site-prompts",
        title: "Development prompts exposed by this site",
        summary: "Guided setup, implementation, diagnosis, and architecture-review workflows.",
        content: [
          "browsermcp_get_started adapts installation steps to an environment. browsermcp_implement guides implementation of a Tool, Resource, Prompt, or connection feature. browsermcp_diagnose builds an evidence-gathering troubleshooting workflow. browsermcp_review_boundaries checks whether logic belongs to the client, Bridge, web library, or app.",
          "Each prompt tells the client to use structured Docs tools and cite page/section paths rather than relying on a free-form summary.",
        ],
        kind: "status",
        status: "implemented",
        tags: [
          "get started prompt",
          "implement prompt",
          "diagnose prompt",
          "responsibility review",
        ],
        related: ["troubleshooting/diagnostic-order", "architecture/responsibility-boundaries"],
        source: "site/src/browsermcp/registration.ts",
      }),
      section({
        id: "prompt-safety",
        title: "Prompt safety",
        summary: "Arguments remain untrusted input and output remains advisory.",
        content: [
          "Limit argument sizes, preserve the distinction between user-provided text and instructions, and never include pairing tokens or secrets in generated messages.",
          "A prompt cannot authorize side effects. Tool execution remains a separate MCP operation subject to client policy and handler validation.",
        ],
        kind: "security",
        status: "implemented",
        tags: ["prompt injection", "untrusted", "secret", "authorization", "limit"],
        related: ["security-model/input-limits"],
        source: "docs/specification.md",
      }),
    ],
  },
  {
    id: "results-errors",
    title: "Results and Errors",
    description:
      "Success content, execution failures, transport errors, timeout, and cancellation.",
    path: "/docs/results-errors",
    order: 10,
    audience: ["app-developer", "contributor"],
    status: "implemented",
    source: "docs/specification.md",
    related: ["tools", "connection-lifecycle", "bridge-protocol"],
    sections: [
      section({
        id: "success-results",
        title: "Success results",
        summary: "Return MCP content plus optional structured content from the browser handler.",
        content: [
          "Tool handlers return a result object containing MCP content items. Prefer concise text for human-readable output and structuredContent for machine-readable data when supported.",
          "Resource and prompt handlers use their standard MCP result shapes. The web library correlates the result with the Bridge request ID.",
        ],
        kind: "api",
        status: "implemented",
        tags: ["result", "content", "structuredContent", "correlation", "success"],
        related: ["bridge-protocol/envelope"],
        source: "docs/specification.md",
      }),
      section({
        id: "errors",
        title: "Execution and protocol errors",
        summary:
          "Expected handler failures are distinct from invalid messages and unavailable runtimes.",
        content: [
          "A handler may throw, but the public handler API does not define application-specific stable error codes. Except for timeout and cancellation, the web runtime normalizes handler exceptions to protocol HANDLER_ERROR with a bounded safe message; stacks are not exposed.",
          "Invalid envelopes, unsupported protocol versions, authentication failures, size-limit violations, and unknown request IDs are rejected before handler dispatch.",
        ],
        kind: "api",
        status: "implemented",
        tags: ["execution error", "protocol error", "invalid message", "safe message", "stack"],
        related: ["troubleshooting/authentication-error", "bridge-protocol/validation"],
        source: "docs/specification.md",
      }),
      section({
        id: "timeout-cancel",
        title: "Timeout and cancellation",
        summary: "Deadlines and AbortSignal stop work across the Bridge–browser boundary.",
        content: [
          "The Bridge rejects a request when its configured deadline expires and sends cancellation to the browser. The web library aborts the handler signal and ignores a late result.",
          "An MCP client cancellation follows the same path. Handlers must observe AbortSignal during fetches, Worker jobs, and loops; cancellation cannot forcibly roll back an already-completed side effect.",
        ],
        kind: "api",
        status: "implemented",
        tags: ["timeout", "cancel", "AbortSignal", "deadline", "late result"],
        related: ["connection-lifecycle/disconnect"],
        source: "docs/specification.md",
      }),
    ],
  },
  {
    id: "connection-lifecycle",
    title: "Connection Lifecycle",
    description:
      "Pairing, session establishment, capability synchronization, reconnect, and teardown.",
    path: "/docs/connection-lifecycle",
    order: 11,
    audience: ["app-developer", "bridge-operator", "contributor"],
    status: "implemented",
    source: "docs/specification.md",
    related: ["bridge-protocol", "security-model", "troubleshooting"],
    sections: [
      section({
        id: "state-machine",
        title: "Connection state machine",
        summary:
          "idle → connecting → awaiting-approval → connected, with reconnecting, disconnecting, disconnected, and error transitions.",
        content: [
          "connect opens the browser WebSocket, negotiates a compatible protocol version and features, and enters awaiting-approval while the Bridge operator verifies the exact Origin. Approval establishes a session and publishes the capability snapshot.",
          "Only a connected, authenticated runtime may receive invocations. The site status panel displays connection and session state separately so an open socket is not mistaken for an active session.",
        ],
        kind: "protocol",
        status: "implemented",
        tags: [
          "state machine",
          "connecting",
          "awaiting approval",
          "connected",
          "disconnecting",
          "error",
          "session",
        ],
        related: ["bridge-protocol/handshake"],
        source: "docs/specification.md",
      }),
      section({
        id: "reconnect",
        title: "Reconnect safely",
        summary: "Use bounded exponential backoff and a fresh authorization path when required.",
        content: [
          "Unexpected retryable network loss schedules bounded deterministic exponential backoff. Reconnect presents the existing session proof only while it remains valid; an expired or rejected credential clears local session state and ends in error with AUTH_REQUIRED or the safe remote error.",
          "The UI exposes a manual reconnect action and recent errors. It does not silently retry invalid authentication forever.",
          "The default MemorySessionStore supports resume only within the same BrowserMCP instance. A full reload or tab close discards it and requires a fresh operator approval request. A custom persistent sessionStore handles bearer credentials and therefore owns secure storage and failure cleanup; reload resume also requires safely restoring the same runtimeId and instanceId, and one instance credential must never be shared by multiple tabs.",
        ],
        kind: "protocol",
        status: "implemented",
        tags: ["reconnect", "deterministic backoff", "expired approval", "manual reconnect"],
        related: ["troubleshooting/bridge-not-connected", "security-model/pairing"],
        source: "docs/specification.md",
      }),
      section({
        id: "disconnect",
        title: "Disconnect and cleanup",
        summary: "Disconnect rejects outstanding work and atomically removes runtime capabilities.",
        content: [
          "An explicit disconnect sends a protocol message when possible, closes the socket, aborts pending handlers, clears session-only credentials, and updates state subscribers.",
          "The Bridge removes every capability belonging to that runtime. Other tabs and apps remain connected and their registrations are unaffected.",
        ],
        kind: "protocol",
        status: "implemented",
        tags: ["disconnect", "cleanup", "pending", "remove capability", "tab"],
        related: ["multiple-applications/runtime-selection"],
        source: "docs/specification.md",
      }),
    ],
  },
  {
    id: "bridge-protocol",
    title: "Bridge Protocol",
    description: "The typed, versioned internal protocol between the Bridge and browser.",
    path: "/docs/bridge-protocol",
    order: 12,
    audience: ["app-developer", "contributor"],
    status: "implemented",
    source: "docs/specification.md",
    related: ["architecture", "connection-lifecycle", "api-reference"],
    sections: [
      section({
        id: "separation",
        title: "Separate from standard MCP",
        summary:
          "The internal wire protocol models runtime routing and does not tunnel raw MCP messages.",
        content: [
          "Standard MCP ends at the Bridge. Browser messages express connect, approval_required, welcome, register, unregister, invoke, result, execution error, cancel, ping, pong, and disconnect operations using Bridge-owned types.",
          "This separation prevents MCP transport details from leaking into web app handlers and keeps the Bridge reusable across applications.",
        ],
        kind: "protocol",
        status: "implemented",
        tags: ["standard MCP", "internal protocol", "separation", "message kind", "tunnel"],
        related: ["architecture/responsibility-boundaries"],
        source: "docs/specification.md",
      }),
      section({
        id: "envelope",
        title: "Envelope and identifiers",
        summary:
          "Every message has a protocol version, message kind, session context, and bounded payload.",
        content: [
          "Handshake messages identify app ID, name, version, Origin, runtime ID, instance ID, protocol versions, and supported features. Request messages carry a unique request ID and capability reference; results and errors reference that ID exactly once.",
          "Version 1 uses exact object schemas: unknown fields are rejected rather than tolerated. Unknown message kinds, missing required fields, duplicate terminal responses, and oversized payloads are also rejected.",
        ],
        kind: "type",
        status: "implemented",
        tags: ["envelope", "requestId", "sessionId", "runtimeId", "instanceId", "version"],
        related: ["multiple-applications/identity"],
        source: "docs/specification.md",
      }),
      section({
        id: "handshake",
        title: "Handshake and negotiation",
        summary: "Version and feature negotiation precede authentication and registration.",
        content: [
          "The browser advertises supported protocol versions and capabilities. The Bridge selects a mutually supported version or rejects the connection with a version mismatch before accepting registrations.",
          "BrowserMCP v1 requires the complete tools, resources, prompts, cancellation, session-resume, and heartbeat capability set. Version 1.1 adds a credential-free approval request and approval_required response; version 1.0 remains a legacy token/resume compatibility path. No registration completes before negotiation and explicit Origin approval.",
        ],
        kind: "protocol",
        status: "implemented",
        tags: [
          "handshake",
          "negotiation",
          "feature",
          "version mismatch",
          "authentication",
          "ping pong",
        ],
        related: ["troubleshooting/version-mismatch"],
        source: "docs/specification.md",
      }),
      section({
        id: "validation",
        title: "Message validation",
        summary:
          "Runtime parsing rejects malformed, unauthenticated, stale, or out-of-order messages.",
        content: [
          "TypeScript types are compile-time guidance, not a security boundary. Both endpoints validate untrusted JSON at runtime, enforce input size and concurrency limits, and check messages against the current state.",
          "Logs record safe error codes, message kinds, and correlation IDs. Known credential keys, token patterns, bearer values, and credential-bearing URL components are redacted; arbitrary text is not a universal secret detector. Site handlers log structural result metadata, never result bodies.",
        ],
        kind: "security",
        status: "implemented",
        tags: [
          "runtime validation",
          "malformed",
          "out of order",
          "size",
          "concurrency",
          "redaction",
        ],
        related: ["security-model/input-limits"],
        source: "docs/specification.md",
      }),
    ],
  },
  {
    id: "security-model",
    title: "Security Model",
    description: "Trust boundaries, Origin-bound pairing, limits, validation, and secret handling.",
    path: "/docs/security-model",
    order: 13,
    audience: ["app-developer", "bridge-operator", "contributor"],
    status: "implemented",
    source: "docs/specification.md",
    related: ["bridge-configuration", "bridge-protocol", "troubleshooting"],
    sections: [
      section({
        id: "trust-boundaries",
        title: "Trust boundaries",
        summary: "MCP client, local Bridge, and browser Origin are three separate principals.",
        content: [
          "Loopback prevents LAN exposure but does not authenticate a local process or arbitrary web page. The browser security principal is the exact Origin. App ID/name/version, runtime ID, and instance ID are self-declared routing metadata, not independently authenticated identities.",
          "Initial operator approval binds only the exact observed Origin. The resulting resume credential is then bound to that Origin plus the declared routing tuple. All code sharing one Origin has the same authority; app IDs do not isolate mutually untrusted same-Origin code.",
          "The paired web app is trusted only for its namespaced capabilities. The MCP client still decides whether to call them, and the app still validates arguments before browser execution.",
        ],
        kind: "security",
        status: "implemented",
        tags: ["trust boundary", "localhost", "loopback", "principal", "isolation"],
        related: ["configuring-mcp-client/client-trust"],
        source: "docs/specification.md",
      }),
      section({
        id: "pairing",
        title: "Origin-bound approval",
        summary:
          "A bounded waiting request receives a session only after an operator approves the exact Origin.",
        content: [
          "The browser requests access without a credential. The Bridge displays the exact observed Origin and page-declared app/runtime identity in its authenticated management page, bounds and expires the pending socket, and creates no session or registrations until an operator chooses Approve. Reject or expiry closes without authority.",
          "Every OWNER.github.io project path shares the OWNER.github.io Origin. Trust all content on that Origin before approval, or use a dedicated custom hostname for isolation. Legacy one-time tokens remain available for compatibility but are transient and omitted from URLs.",
        ],
        kind: "security",
        status: "implemented",
        tags: [
          "Origin error",
          "authentication error",
          "approval",
          "expiry",
          "rejection",
          "impersonation",
        ],
        related: ["troubleshooting/origin-error", "troubleshooting/authentication-error"],
        source: "docs/specification.md",
      }),
      section({
        id: "input-limits",
        title: "Limits and rejection",
        summary:
          "Bound message size, concurrent requests, execution time, and accepted protocol shapes.",
        content: [
          "The Bridge and web library enforce conservative maximum payload sizes, a per-runtime concurrent request limit, request deadlines, and cancellation. Apps add tighter schema limits appropriate to each capability.",
          "Reject unknown message kinds, incompatible versions, unauthenticated registration, unknown capabilities, invalid request IDs, and malformed JSON. Fail closed rather than guessing a compatible interpretation.",
        ],
        kind: "security",
        status: "implemented",
        tags: ["input size", "concurrency", "timeout", "unknown message", "fail closed"],
        related: ["bridge-protocol/validation", "tools/tool-safety"],
        source: "docs/specification.md",
      }),
      section({
        id: "known-boundaries",
        title: "Known security boundaries",
        summary:
          "Browser sandboxing and local pairing reduce risk but do not make arbitrary tools safe.",
        content: [
          "A capability runs with the privileges of its page Origin and can access that Origin's browser storage. BrowserMCP does not bypass same-origin policy, permission prompts, secure-context requirements, or user-gesture requirements.",
          "Compromise of any code at a paired Origin can affect that Origin's capabilities and data until disconnected. Version 0.1 has no individual revoke control: disconnect or close the affected tab, or restart the Bridge to revoke all in-memory authority.",
        ],
        kind: "security",
        status: "constraint",
        tags: ["known constraint", "browser sandbox", "same-origin", "permission", "compromise"],
        related: ["roadmap/security-hardening"],
        source: "Browser security model",
      }),
    ],
  },
  {
    id: "multiple-applications",
    title: "Multiple Applications",
    description: "Identity, namespaces, tabs, collision handling, and deterministic routing.",
    path: "/docs/multiple-applications",
    order: 14,
    audience: ["app-developer", "bridge-operator", "contributor"],
    status: "implemented",
    source: "docs/specification.md",
    related: ["architecture", "bridge-protocol", "troubleshooting"],
    sections: [
      section({
        id: "identity",
        title: "Application and runtime identity",
        summary: "Stable app identity is distinct from Origin, runtime ID, and tab instance ID.",
        content: [
          "An app self-declares an app ID, name, version, runtime ID, and instance ID for namespacing and routing. The WebSocket supplies the exact Origin, which is the browser-authenticated principal.",
          "The Bridge never treats a display name or routing tuple as an independently authenticated security identity. After exact-Origin approval or legacy token pairing, the resume token is bound to the declared tuple. Same-Origin code can claim that tuple, so use Origin separation for mutually untrusted apps.",
        ],
        kind: "architecture",
        status: "implemented",
        tags: ["appId", "app name", "Origin", "runtimeId", "instanceId", "tab"],
        related: ["bridge-protocol/envelope"],
        source: "docs/specification.md",
      }),
      section({
        id: "namespacing",
        title: "Names and collisions",
        summary:
          "Bridge-visible names include app namespace; ambiguous runtimes never silently replace each other.",
        content: [
          "A local tool such as search is exposed with an app-qualified name. Different apps may register the same local name because the Bridge namespace differs.",
          "If the same app and Origin register the same capability from multiple tabs, the Bridge reports an ambiguous target and rejects invocation. Version 0.1 has no client instance-selection field or configurable routing policy; close duplicate providers and retry. Silent last-writer-wins routing is forbidden.",
        ],
        kind: "architecture",
        status: "implemented",
        tags: ["name collision", "namespace", "multiple candidates", "conflict", "last writer"],
        related: ["troubleshooting/name-conflict"],
        source: "docs/specification.md",
      }),
      section({
        id: "runtime-selection",
        title: "Runtime selection and removal",
        summary:
          "Reject ambiguous duplicate instances and remove only the disconnected instance's capabilities.",
        content: [
          "Capability descriptors retain app, Origin, runtime, and instance routing metadata, but the standard MCP surface in version 0.1 cannot explicitly select an instance. Exactly one provider must remain. A target that disconnects returns unavailable and is not rerouted silently.",
          "Disconnect atomically removes that runtime's capabilities and cancels its in-flight requests. Other app and tab registrations stay intact.",
        ],
        kind: "architecture",
        status: "implemented",
        tags: ["target", "routing", "disconnect", "unavailable", "atomic remove"],
        related: ["connection-lifecycle/disconnect"],
        source: "docs/specification.md",
      }),
    ],
  },
  {
    id: "api-reference",
    title: "API Reference",
    description: "Public BrowserMCP APIs and types used by web applications.",
    path: "/docs/api-reference",
    order: 15,
    audience: ["app-developer", "contributor"],
    status: "implemented",
    source: "@browsermcp/web public API",
    related: ["creating-app", "tools", "resources", "prompts"],
    sections: [
      section({
        id: "browsermcp",
        title: "BrowserMCP class",
        summary:
          "Owns one app runtime, registrations, connection state, requests, logs, and cleanup.",
        content: [
          "new BrowserMCP({ name, version, ...identity }) creates a disconnected app. tool, resource, and prompt add declarative registrations. connect starts negotiation and authentication; disconnect stops it; reconnect starts a bounded new attempt.",
          "Subscribe to state and diagnostic events for UI. Do not depend on protocol message internals; public app APIs and the internal protocol are intentionally separate.",
        ],
        kind: "api",
        status: "implemented",
        tags: [
          "BrowserMCP",
          "class",
          "constructor",
          "connect",
          "disconnect",
          "reconnect",
          "subscribe",
        ],
        related: ["creating-app/minimal-app", "connection-lifecycle/state-machine"],
        source: "packages/web/src public exports",
      }),
      section({
        id: "registration-types",
        title: "Tool, Resource, and Prompt definitions",
        summary:
          "Typed declarations pair metadata and runtime-validated inputs with async handlers.",
        content: [
          "ToolDefinition contains name, description, inputSchema, and handler. ResourceDefinition contains uri, name, optional description and mimeType, and handler. PromptDefinition contains name, description, argument metadata, and handler.",
          "Names and URIs are stable app-local identifiers. Registration returns a cleanup function or handle, and duplicate local registration is rejected rather than overwritten.",
        ],
        kind: "type",
        status: "implemented",
        tags: [
          "ToolDefinition",
          "ResourceDefinition",
          "PromptDefinition",
          "type",
          "handler",
          "duplicate",
        ],
        related: ["tools/register-tool", "resources/register-resource", "prompts/register-prompt"],
        source: "packages/web/src public types",
      }),
      section({
        id: "connection-types",
        title: "Connection and diagnostics types",
        summary:
          "BrowserMCPOptions, ConnectOptions, state snapshots, execution records, and safe logs.",
        content: [
          "BrowserMCPOptions supplies the loopback bridgeUrl, app identity, reconnect and timeout policy, approval wait limit, session store, and optional Local Network Access preparation. ConnectOptions supplies an approval-request policy, optional legacy pairing token, AbortSignal, and per-attempt LNA preparation; resume credentials remain internal.",
          "BrowserMCPSnapshot exposes ConnectionState, public session ID and expiry, negotiated capabilities, limits, RegistrationSnapshot entries, ExecutionRecord history, lastError, and reconnect attempt. BrowserMCPLogEntry values are bounded, deep-frozen, and deep-cloned for each external reader; recognized credential fields and URL credentials are redacted, and handler result bodies are never copied into logs. This is defense in depth rather than a universal arbitrary-secret detector.",
        ],
        kind: "type",
        status: "implemented",
        tags: [
          "BrowserMCPOptions",
          "ConnectOptions",
          "BrowserMCPSnapshot",
          "ConnectionState",
          "ExecutionRecord",
          "BrowserMCPLogEntry",
          "timeout",
          "token",
        ],
        related: ["results-errors/errors"],
        source: "packages/web/src public types",
      }),
      section({
        id: "protocol-types",
        title: "Protocol types are internal",
        summary:
          "Shared wire types are exported separately and are not required by normal app handlers.",
        content: [
          "@browsermcp/protocol provides validators and discriminated message types for Bridge and web library implementers. Application code normally imports only @browsermcp/web.",
          "Protocol types may evolve under negotiated versioning; applications should not send raw messages or treat the WebSocket as standard MCP.",
        ],
        kind: "type",
        status: "implemented",
        tags: [
          "@browsermcp/protocol",
          "internal API",
          "wire type",
          "validator",
          "discriminated union",
        ],
        related: ["bridge-protocol/separation"],
        source: "docs/specification.md",
      }),
    ],
  },
  {
    id: "bridge-configuration",
    title: "Bridge Configuration",
    description: "Loopback endpoints, Origin policy, limits, logs, and operational defaults.",
    path: "/docs/bridge-configuration",
    order: 16,
    audience: ["bridge-operator", "contributor"],
    status: "implemented",
    source: "Cross-platform Bridge configuration",
    related: ["installing-macos-bridge", "security-model", "troubleshooting"],
    sections: [
      section({
        id: "network",
        title: "Network endpoints",
        summary: "MCP and browser transports share port 8789 and bind only to 127.0.0.1.",
        content: [
          "The default MCP endpoint is http://127.0.0.1:8789/mcp. The browser endpoint is ws://127.0.0.1:8789/browser. The host is fixed to 127.0.0.1 and is not configurable.",
          "Port overrides are local operator configuration and must be reflected in both the MCP client and web app connection UI. LAN and external binding are unsupported.",
        ],
        kind: "configuration",
        status: "implemented",
        tags: ["host", "port", "8789", "mcp path", "browser path", "loopback"],
        related: ["configuring-mcp-client/client-entry"],
        source: "Cross-platform Bridge defaults",
      }),
      section({
        id: "https-site",
        title: "HTTPS-hosted site and local WSS",
        summary:
          "Published HTTPS pages use trusted wss://127.0.0.1:8789 and explicit approval of the exact observed Origin.",
        content: [
          "Start the Bridge with --tls-cert and --tls-key using a certificate whose SAN includes 127.0.0.1. The secure browser endpoint is wss://127.0.0.1:8789/browser and health is https://127.0.0.1:8789/health. IPv4 avoids a localhost-to-IPv6 resolution mismatch with the loopback-only listener.",
          "Request approval from the site, then approve its exact observed Origin in the Bridge management page. For GitHub Pages the Origin contains the scheme and host, not the repository subpath. All OWNER.github.io project sites share that Origin; use a dedicated custom hostname when they must not share trust.",
          "Trust only the local CA through the active OS/browser mechanism before connecting: macOS Keychain, a Linux distribution/browser store, or the Windows current-user Trusted Root store. The Bridge uses the same PEM inputs on all platforms and never installs trust. On Chromium, use the credential-free /health fetch to request loopback-network and any OS local-network permission before WSS. Chrome 142 applies LNA to fetch-like requests and Chrome 147 extends it to WebSockets; this is current LNA permission, not legacy PNA preflight. Never downgrade an HTTPS page to ws://.",
        ],
        kind: "configuration",
        status: "implemented",
        tags: [
          "HTTPS",
          "WSS",
          "127.0.0.1",
          "certificate",
          "Origin allow",
          "Private Network Access",
          "Safari",
        ],
        related: ["development/github-pages-build", "troubleshooting/origin-error"],
        source: "Bridge TLS configuration; site/src/runtime/bridge-config.ts",
      }),
      section({
        id: "limits",
        title: "Request limits",
        summary: "Configure bounded payload, concurrency, deadline, and idle-session policies.",
        content: [
          "Bridge defaults are deliberately finite. Operators may lower limits for their environment, but raising them increases memory and denial-of-service risk and does not override per-tool schemas.",
          "Timeouts should cover end-to-end browser execution. Idle session expiry and ping intervals remove orphaned runtimes without forcing active requests to poll.",
        ],
        kind: "configuration",
        status: "implemented",
        tags: ["max payload", "concurrency", "deadline", "idle session", "ping"],
        related: ["security-model/input-limits", "results-errors/timeout-cancel"],
        source: "docs/specification.md",
      }),
      section({
        id: "secrets-logs",
        title: "Approval and log configuration",
        summary:
          "Approval state is bounded and ephemeral; logs are structured, bounded, and redacted.",
        content: [
          "The Bridge keeps pending Origin approval requests bounded and short-lived and does not ship a fixed browser secret. Legacy pairing tokens are generated only on demand. Environment variables or CLI arguments configure operational values, not long-lived browser credentials.",
          "Diagnostic retention is bounded. Recognized credential field names, authorization headers, known token patterns, and credential-bearing URL components are redacted before storage or display. Arbitrary text cannot be guaranteed secret-free, so handler result bodies are not logged.",
        ],
        kind: "configuration",
        status: "implemented",
        tags: ["environment variable", "CLI", "secret", "log", "redact", "retention"],
        related: ["security-model/pairing"],
        source: "docs/specification.md; cross-platform Bridge implementation",
      }),
    ],
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    description:
      "Evidence-first diagnosis for connection, Origin, auth, version, and routing failures.",
    path: "/docs/troubleshooting",
    order: 17,
    audience: ["app-developer", "bridge-operator", "contributor"],
    status: "implemented",
    source: "docs/specification.md",
    related: ["connection-lifecycle", "security-model", "multiple-applications"],
    sections: [
      section({
        id: "diagnostic-order",
        title: "Diagnostic order",
        summary: "Check listener, pairing, session, registration, routing, then handler execution.",
        content: [
          "Start with the Bridge health and exact loopback endpoints. Next compare the browser's exact Origin with the paired Origin, inspect authentication and protocol negotiation, confirm an active session, and inspect registered capability names.",
          "Only after routing succeeds should you debug the browser handler. Correlate the MCP request with Bridge and site history using request IDs; do not expose tokens while collecting logs.",
        ],
        kind: "troubleshooting",
        status: "implemented",
        tags: ["diagnose", "order", "listener", "session", "registration", "request ID"],
        related: ["results-errors/errors"],
        source: "docs/specification.md",
      }),
      section({
        id: "bridge-not-connected",
        title: "Bridge not connected",
        summary:
          "Distinguish an unavailable listener, unpaired page, expired session, and registration failure.",
        content: [
          "Confirm the Bridge is running and bound to 127.0.0.1:8789. In the site panel, check the implemented idle, connecting, connected, reconnecting, disconnecting, disconnected, or error state and read the most recent safe error; AUTH_REQUIRED is an error code, not a separate connection state.",
          "If the MCP client connects but capabilities are absent, the browser session is missing or registrations were rejected. Pair the exact Origin, reconnect once, then inspect Bridge app and capability lists.",
        ],
        kind: "troubleshooting",
        status: "implemented",
        tags: ["Bridge disconnected", "not connected", "no capabilities", "listener", "reconnect"],
        related: ["connection-lifecycle/reconnect", "installing-macos-bridge/start"],
        source: "docs/specification.md",
      }),
      section({
        id: "origin-error",
        title: "Origin rejected",
        summary:
          "The page scheme, host, or port does not exactly match the paired Origin or allow policy.",
        content: [
          "Read location.origin in the site runtime panel and compare it character-for-character with the Origin shown by the Bridge pairing record. localhost and 127.0.0.1 are different hosts; a changed Vite port is also a different Origin.",
          "Request access again and approve the exact Origin shown in the Bridge management page. Never broaden the Origin check to '*' or disable validation as a workaround.",
        ],
        kind: "troubleshooting",
        status: "implemented",
        tags: ["Origin error", "origin rejected", "localhost", "127.0.0.1", "port", "CORS"],
        related: ["security-model/pairing"],
        source: "docs/specification.md",
      }),
      section({
        id: "authentication-error",
        title: "Authentication failed",
        summary:
          "Approval was rejected or expired, or a legacy token/resume credential is invalid.",
        content: [
          "For APPROVAL_REJECTED, verify that the operator intended to reject the displayed exact Origin. For APPROVAL_EXPIRED, keep the page open, request again, and decide before the deadline.",
          "Legacy AUTH_INVALID or SESSION_RESUME_REJECTED failures require a fresh explicit approval or a newly issued compatibility token. Use redacted error codes and timestamps; never paste credentials into logs or an issue.",
        ],
        kind: "troubleshooting",
        status: "implemented",
        tags: ["authentication error", "invalid token", "expired", "consumed", "replay"],
        related: ["installing-macos-bridge/pair-site", "security-model/pairing"],
        source: "docs/specification.md",
      }),
      section({
        id: "name-conflict",
        title: "Capability name conflict",
        summary: "Multiple runtimes match an unqualified invocation.",
        content: [
          "Inspect the conflict error's app, Origin, runtime, and instance candidates. Version 0.1 exposes no instance-selection input on standard MCP, so disconnect or close stale duplicate tabs and retry.",
          "Do not resolve collisions by changing Bridge order or relying on the most recently connected tab. The deterministic default is to reject ambiguity.",
        ],
        kind: "troubleshooting",
        status: "implemented",
        tags: ["collision", "ambiguous", "multiple apps", "multiple tabs", "instance ID"],
        related: ["multiple-applications/namespacing"],
        source: "docs/specification.md",
      }),
      section({
        id: "version-mismatch",
        title: "Protocol version mismatch",
        summary: "Bridge and web library have no mutually supported protocol version.",
        content: [
          "Compare the Bridge version, @browsermcp/web version, and advertised protocol versions in safe diagnostics. Build all workspaces from the same checkout before investigating the network.",
          "Do not coerce or ignore the mismatch. Upgrade the older component to a compatible release and reconnect with a fresh session.",
        ],
        kind: "troubleshooting",
        status: "implemented",
        tags: ["version mismatch", "incompatible", "protocol version", "upgrade"],
        related: ["bridge-protocol/handshake"],
        source: "docs/specification.md",
      }),
    ],
  },
  {
    id: "development",
    title: "Development",
    description:
      "Workspace structure, site architecture, quality gates, and contribution boundaries.",
    path: "/docs/development",
    order: 18,
    audience: ["contributor"],
    status: "implemented",
    source: "Repository implementation",
    related: ["architecture", "api-reference", "roadmap"],
    sections: [
      section({
        id: "workspace",
        title: "Workspace layout",
        summary:
          "Protocol, web library, cross-platform Bridge, native macOS app, Vite site, tests, and docs remain separate.",
        content: [
          "The npm workspace keeps shared protocol definitions separate from the public web library. /site consumes both through workspace dependencies; /bridge implements the common local Node.js service; /macOS contains only the SwiftUI/AppKit lifecycle shell. Repository tests cover unit and end-to-end request behavior.",
          "Use Node.js 24 or newer. Preserve ESM and strict TypeScript boundaries, keep Bridge runtime paths OS-neutral, and avoid adding dependencies when platform APIs are sufficient. Native app work also requires macOS 14+ and current Xcode.",
        ],
        kind: "architecture",
        status: "implemented",
        tags: ["monorepo", "workspace", "Node 24", "ESM", "strict TypeScript", "dependency"],
        related: ["getting-started/prerequisites"],
        source: "Repository layout and package.json",
      }),
      section({
        id: "site-architecture",
        title: "Site architecture",
        summary:
          "Framework-free Vite UI, typed docs corpus, pure search service, and one registration adapter.",
        content: [
          "The site renders routes without a UI framework. src/docs/content.ts is the single source for both visible docs and MCP responses; search operates over a precomputed structured index, never the DOM.",
          "src/browsermcp/registration.ts is the integration boundary for the repository web library. Runtime utilities isolate IndexedDB and Worker behavior, with size limits and no arbitrary code execution.",
          "src/i18n provides nine allowlisted display locales, browser preference resolution, one non-secret persisted override, RTL document direction for Arabic, localized UI, stable-ID technical-prose overlays, and multilingual search aliases. Locale changes rerender the same hash route and BrowserMCP controller without reconnecting or changing stable documentation identifiers. Code examples, commands, URLs, API/MCP identifiers, and machine-readable Docs responses retain the traceable English source corpus.",
        ],
        kind: "architecture",
        status: "implemented",
        tags: [
          "Vite",
          "framework-free",
          "structured docs",
          "search index",
          "registration adapter",
          "i18n",
          "localization",
          "RTL",
        ],
        related: ["tools/site-tools", "resources/site-resources"],
        source: "site/src",
      }),
      section({
        id: "github-pages-build",
        title: "Build for a GitHub Pages subpath",
        summary:
          "Use a relative or explicit Vite base, hash routing, WSS loopback TLS, and the exact published Origin.",
        content: [
          "The default Vite base is relative and every application route is encoded after #, so dist can be hosted below a repository path without server rewrites. Set VITE_BASE_PATH=/BrowserMCP/ when an explicit Pages repository base is preferred, then run npm run build:site. The pinned .github/workflows/pages.yml pipeline runs npm run check on main, rebuilds with the Pages-provided base_path, and deploys only site/dist.",
          'On Windows PowerShell, set $env:VITE_BASE_PATH = "/BrowserMCP/", run npm run build:site, then Remove-Item Env:VITE_BASE_PATH. POSIX inline environment assignment is only for macOS/Linux shells.',
          "An HTTPS page must connect to wss://127.0.0.1:8789/browser. Generate the local development CA and IP-valid certificate with npm run tls:generate; the Node helper invokes OpenSSL without a shell and uses native paths on macOS, Linux, and Windows. Trust only the CA through the active OS/browser store and start the Bridge with --tls-cert and --tls-key. Request access from the page and approve the exact Pages Origin, such as https://owner.github.io; the /BrowserMCP/ path is not part of Origin.",
          "Before WebSocket setup, use the site's credential-free local access check. Resolve certificate trust at https://127.0.0.1:8789/health, then allow loopback-network and any OS local-network permission when prompted. Chrome 142 gates fetch-like local requests and Chrome 147 gates WebSockets; this is current LNA permission rather than legacy PNA preflight. Safari rejects HTTPS-to-ws mixed content.",
          "Every https://owner.github.io/<repo>/ project shares the https://owner.github.io Origin, so approval cannot isolate /BrowserMCP/. Trust every same-Origin project or use a dedicated custom hostname.",
        ],
        kind: "guide",
        status: "implemented",
        tags: ["GitHub Pages", "subpath", "VITE_BASE_PATH", "hash routing", "WSS", "TLS", "LNA"],
        examples: [
          {
            id: "pages-build",
            title: "Produce a static Pages-compatible build",
            language: "shell",
            code: "VITE_BASE_PATH=/BrowserMCP/ npm run build:site\nnpm run tls:generate -- .browsermcp/tls\nnpm run start:bridge -- --port 8789 --tls-cert .browsermcp/tls/localhost-cert.pem --tls-key .browsermcp/tls/localhost-key.pem",
            explanation:
              "Use the certificate paths printed by tls:generate if they differ. Never place TLS or Bridge credentials in the Pages artifact or workflow.",
          },
        ],
        related: ["bridge-configuration/https-site", "security-model/pairing"],
        source: "site/vite.config.ts; scripts/generate-local-tls.mjs; Bridge CLI",
      }),
      section({
        id: "quality-gates",
        title: "Quality gates",
        summary:
          "Format, typecheck, unit tests, integration tests, and production build are required.",
        content: [
          "Run npm run format:check, npm run typecheck, npm test, and npm run build from the repository root. Site evaluation tests cover the ten required Docs MCP scenarios and verify source/page/section traceability.",
          "Build and test macOS/BrowserMCPApp.xcodeproj with code signing disabled when the native app changes. Browser, native GUI, Linux, Windows, and Intel Mac checks must record prerequisites, steps, expected result, and known limits when automation cannot exercise the environment.",
        ],
        kind: "guide",
        status: "implemented",
        tags: ["format", "typecheck", "unit test", "integration test", "build", "quality"],
        related: ["getting-started/workspace-setup"],
        source: "docs/specification.md",
      }),
      section({
        id: "contributing",
        title: "Contribution rules",
        summary: "Keep protocol, Bridge, web library, app behavior, docs, and tests synchronized.",
        content: [
          "Add protocol behavior only when it is app-agnostic. Keep application business logic in the browser app and public ergonomics in the web library. Update structured docs and tests in the same change as public behavior.",
          "Never commit fixed secrets, personal paths, generated credentials, signing material, or generated deployment output. The only publishing workflow deploys site/dist to GitHub Pages after the main quality gates; package/release publishing, signing, and notarization remain out of scope.",
        ],
        kind: "guide",
        status: "implemented",
        tags: ["contribution", "boundary", "docs", "test", "secret", "out of scope"],
        related: ["architecture/responsibility-boundaries"],
        source: "docs/specification.md",
      }),
    ],
  },
  {
    id: "roadmap",
    title: "Roadmap",
    description: "Implemented scope, planned work, and known constraints.",
    path: "/docs/roadmap",
    order: 19,
    audience: ["app-developer", "bridge-operator", "contributor"],
    status: "partial",
    source: "docs/specification.md; repository status",
    related: ["development", "security-model", "multiple-applications"],
    sections: [
      section({
        id: "implemented",
        title: "Implemented in this repository",
        summary:
          "Typed protocol, web library, portable Bridge, native macOS app, Vite site, Docs MCP, and tests.",
        content: [
          "The repository implements the initial BrowserMCP round trip, capability registration, connection lifecycle, authentication boundary, namespaced multiple-app routing, a cross-platform Node.js Bridge, a native macOS menu-bar lifecycle app, this framework-free Vite site, and source-backed Docs tools, resources, and prompts.",
          "Implementation status in this corpus is explicit at page and section level and is returned by search and capability queries.",
        ],
        kind: "status",
        status: "implemented",
        tags: ["implemented", "current", "capability", "docs MCP", "tests"],
        related: ["tools/site-tools", "development/quality-gates"],
        source: "Repository implementation",
      }),
      section({
        id: "verification-status",
        title: "Real-environment verification status",
        summary:
          "Automated gates and the published Pages MCP round trip pass; manual trust UI and additional browsers remain environment-dependent.",
        content: [
          "Node unit and integration tests cover structured Docs evaluation, registration, validated Bridge Protocol round trips, secure URL policy, subpath build logic, storage limits, Worker cancellation, and capability status. GitHub Pages run 29643866925 also passed the complete quality gate and deployed the public site.",
          "An isolated installed-Chrome session loaded https://masashi-desu.github.io/BrowserMCP/, reached the loopback TLS Bridge after a context-scoped Local Network Access grant, received exact-Origin approval, registered 19 Tools, 23 Resources, and four Prompts, and served docs_get_section to the official MCP SDK client. The context ignored loopback TLS errors, so manual OS CA trust, interactive permission UI, Safari, Edge, and Firefox remain unverified and are tracked in docs/verification.md.",
        ],
        kind: "status",
        status: "partial",
        tags: ["partial", "verification", "browser", "TLS", "LNA", "MCP client", "capabilities"],
        related: ["development/quality-gates", "development/github-pages-build"],
        source: "site tests and site/README.md manual verification",
      }),
      section({
        id: "distribution",
        title: "Distribution and broader platform support",
        summary:
          "Package/release publishing, signing, notarization, auto-update, bundled Node.js, and non-Apple-Silicon verification remain undone.",
        content: [
          "The static documentation site is deployed to GitHub Pages from main. Packages, the Bridge, and the native macOS app are structured for future distribution, but this work does not publish to npm, GitHub Releases, or the App Store and does not sign or notarize BrowserMCP.app.",
          "The Bridge targets macOS, Linux, and Windows, but only Apple Silicon macOS is verified. Linux, Windows, Intel Mac, bundled Node.js, OS service installation, automatic updates, and stable installer packaging require later validation or distribution work.",
        ],
        kind: "status",
        status: "planned",
        tags: [
          "unimplemented",
          "planned",
          "npm publish",
          "App Store",
          "signing",
          "Linux",
          "Windows",
          "Intel",
        ],
        related: ["installing-macos-bridge/build"],
        source: "docs/specification.md",
      }),
      section({
        id: "security-hardening",
        title: "Future security hardening",
        summary:
          "External review, richer revocation UX, and packaged credential storage are planned.",
        content: [
          "The initial implementation enforces loopback binding, Origin-bound short-lived pairing, runtime validation, limits, timeouts, cancellation, and log redaction. Independent security review and hardened release credential storage are not part of this local build.",
          "Future work may add richer per-capability consent and audit export while preserving the boundary that the Bridge contains no app-specific business logic.",
        ],
        kind: "status",
        status: "planned",
        tags: ["planned", "security review", "revocation", "consent", "audit"],
        related: ["security-model/known-boundaries"],
        source: "Repository status",
      }),
      section({
        id: "known-constraints",
        title: "Known constraints",
        summary:
          "Verified-platform scope, external Node.js, browser rules, and ambiguity rejection are explicit limits.",
        content: [
          "The verified host is Apple Silicon macOS with Node.js 24+. Linux, Windows, and Intel Mac are unverified. The native app is macOS 14+ only, unsigned, requires external Node.js 24, and disables App Sandbox to launch operator-selected executables/files; the common Bridge itself installs no OS service and changes no certificate trust.",
          "Browser handlers remain subject to same-origin policy, secure contexts, permissions, user gestures, tab lifetime, and browser resource limits. Multiple matching tabs must be resolved outside the call by closing or disconnecting duplicates; version 0.1 has no instance-selection UI/API and no silent fallback. No cloud relay, external server, or remote-browser transport is implemented.",
        ],
        kind: "status",
        status: "constraint",
        tags: [
          "constraint",
          "macOS",
          "Linux",
          "Windows",
          "external Node.js",
          "browser permission",
          "tab lifetime",
          "no cloud",
        ],
        related: ["multiple-applications/namespacing", "security-model/known-boundaries"],
        source: "docs/specification.md; repository status",
      }),
    ],
  },
] as const;

export const docsById = new Map(docs.map((page) => [page.id, page]));

export const requiredDocPageIds = [
  "introduction",
  "architecture",
  "getting-started",
  "installing-macos-bridge",
  "configuring-mcp-client",
  "creating-app",
  "tools",
  "resources",
  "prompts",
  "results-errors",
  "connection-lifecycle",
  "bridge-protocol",
  "security-model",
  "multiple-applications",
  "api-reference",
  "bridge-configuration",
  "troubleshooting",
  "development",
  "roadmap",
] as const;
